import { useState } from 'react';
import { IconButton } from 'src/components/Buttons';
import { Content } from 'src/contexts/ContentContext';
import { useReportedMissingSections } from 'src/contexts/ReportedMissingSectionsContext';
import { vars } from 'src/core/themes';
import { HoverableIcon } from 'src/core/ui/HoverableIcon/HoverableIcon';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ClockIcon, IconsFill, WarningOutlineIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { Feature, Permission } from 'src/WebApiController';

import { VenueMapReportModal } from './VenueMapReportModal';

export type ReportMapIssueButtonProps = {
  /** Viagogo/StubHub event id the map belongs to; null hides the action. */
  eventId: number | null | undefined;
  venueConfigId: number | null | undefined;
};

/**
 * Self-contained "Report map issue" action for a venue map (POS-9306). Shows a
 * report icon, or a "requested" clock icon once the venue config has an
 * outstanding map report. Renders nothing unless the catalog-support feature and
 * inventory-edit permission are present and the event is mapped.
 */
export const ReportMapIssueButton = ({
  eventId,
  venueConfigId,
}: ReportMapIssueButtonProps) => {
  const hasFeature = useUserHasFeature(Feature.CatalogSupport);
  const hasPermission = useUserHasAnyOfPermissions(
    Permission.Inventory_UpdateListingInfo,
    Permission.Inventory_UpdateListingInfoOnIsPricerOf,
    Permission.Inventory_UpdateListingInfoOnPurchasedBy
  );
  const { isMapReported, markMapReported } = useReportedMissingSections();
  const [isOpen, setIsOpen] = useState(false);

  if (!hasFeature || !hasPermission || eventId == null) {
    return null;
  }

  // A venue-map-level report already exists for this venue config or event
  // (section name is not considered at this level).
  const alreadyReported = isMapReported(venueConfigId, eventId);

  if (alreadyReported) {
    return (
      <HoverableIcon
        icon={
          <IconButton
            icon={
              <ClockIcon
                fill={IconsFill.textBrand}
                size={vars.iconSize.l}
                noOutline
              />
            }
            disabled
            disableCursor
          />
        }
        tooltip={<Content id={ContentId.MapIssueRequested} />}
      />
    );
  }

  return (
    <>
      <HoverableIcon
        icon={
          <IconButton
            icon={
              <WarningOutlineIcon
                fill={IconsFill.textBrand}
                size={vars.iconSize.m}
                withHoverEffect
              />
            }
            onClick={() => setIsOpen(true)}
          />
        }
        tooltip={<Content id={ContentId.ReportMapIssue} />}
      />
      <VenueMapReportModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        eventIds={[eventId]}
        venueConfigId={venueConfigId ?? null}
        onSubmitted={() => markMapReported(venueConfigId, eventId)}
      />
    </>
  );
};
